// Flag types indicate the status of the flag.
// The ordering of this enum is also used to determine the order in which the flags appear in the admin dashboard
export enum FlagType {
  Product = 'Product',
  Configuration = 'Configuration',
  // Beta flags should be removed once the feature is released
  Beta = 'Beta',
  Deprecated = 'Deprecated',
  // Alpha or in development
  Alpha = 'Alpha',
  // Configurations, but lesser used
  Tweaks = 'Tweaks',
  // Customer specific flags.
  Customer = 'Customer',
  // Performance related flags for features that need to be tweaked to be performant on some orgs.
  Performance = 'Performance',
}

export const flagTypeDescriptions: Record<FlagType, string> = {
  [FlagType.Product]: 'Full products in FOSSA (Released)',
  [FlagType.Configuration]: 'Configuration options for FOSSA organizations',
  [FlagType.Beta]: 'Stable but officially unreleased features',
  [FlagType.Deprecated]: 'Deprecated features in FOSSA',
  [FlagType.Alpha]: 'In development or unstable features',
  [FlagType.Tweaks]: 'Tweaks to FOSSA features',
  [FlagType.Customer]: 'Flags meant for specific customers',
  [FlagType.Performance]: 'Performance related flags for features that need to be tweaked to be performant on some orgs',
};

// All flags added must be added and documented to the `flagDescriptions`
// object below. This must be actively maintained by engineers
export const AMAZON_LINUX_SUPPORT = 'amazonLinuxEnabled';
export const ARCHIVE_AND_LICENSE_UPLOAD = 'archiveAndLicenseUpload';
export const CLI_DEFAULT_TO_FIRST_PARTY_SCANS = 'cliDefaultToFirstPartyScans';
export const CLI_LICENSE_SCAN_REQUIRE_FULL_FILE_UPLOADS = 'cliLicenseScanRequireFullFileUploads';
export const DEDUPE_LOCATORS_IN_PLAINTEXT_REPORTS = 'dedupeLocatorsInPlainTextReports';
export const DEPENDENCY_PATH_GLOBAL_ISSUES_REPORT = 'dependencyPathGlobalIssuesReport'; // This is just for large orgs
export const DISABLE_ORG_ALERT_BANNER = 'disableOrgAlertBanner';
export const EXCLUDES_DEPENDENCY_PATHS_FROM_ISSUES_CSV_EXPORT = 'excludeDependencyPathsFromIssuesCsvExport';
export const FILE_PATH_IN_REPORTS = 'filePathInReports';
export const GROUPED_SLACK_NOTIFICATIONS = 'groupedSlackNotifications';
export const LDAP_AUTHENTICATION = 'ldapAuthentication';
export const QUALITY = 'quality';
export const REQUIRE_POLICY_ON_UPLOAD = 'requirePolicyOnUpload';
export const REQUIRE_TEAM_ON_UPLOAD = 'requireTeamOnUpload';
export const SECURITY = 'security';
export const TEAM_UNIQUE_ID = 'teamUniqueId';
export const TEAM_UNIQUE_ID_IN_SAML = 'teamUniqueIdInSAML';
export const VENDORED_DEPENDENCY_DEFAULT_TO_ARCHIVE_UPLOAD = 'vendoredDependencyDefaultToArchiveUpload';
export const VULN_ADMIN = 'vulnAdmin';
export const PER_PROJECT_USER_DEFINED_DEPS = 'perProjectUserDeps';
export const SECURITY_PDF_REPORTS = 'securityPDFReports';
export const SBOM_PORTAL = 'sbomPortal';
export const SBOM_PORTAL_PRIVATE = 'sbomPortalPrivate';
export const ISSUES_CREATE_V2 = 'issuesCreateV2';
export const REPORT_COPYRIGHT_SANITIZATION = 'reportCopyrightSanitization';
export const MANUAL_MALWARE = 'manualMalware';
export const ISSUES_MALWARE = 'issuesMalware';
export const REPORTS_V2 = 'reportsV2';
export const MFA = 'mfa';
export const MANUAL_DEPENDENCIES_REVAMP = 'manualDependenciesRevamp';
export const SAVED_REPORT_OPTIONS = 'savedReportOptions';

/**
 * Turns off the Deps V2 rewrite of the Global Package Bundle Report and uses the old version.
 */
export const DISABLE_ORG_BOM_V2 = 'disableOrgBomV2';
export const ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS = 'enableDownloadingLargeProjectLicensingReports';

/**
 * Snippet feature flags
 */
export const SNIPPET_DETECTION = 'snippetDetection';

/**
 * Vendored Dependency flag
 */
export const VENDORED_DEPENDENCY_DETECTION = 'vendoredDependencyDetection';

/**
 * DepsV3 Feature Flags
 */
export const DEPSV3_USE_SPARKLE = 'depsv3UseSparkle';

/**
 * Sparkle feature flags
 */
export const CPE_SEARCH_USE_SPARKLE = 'cpeSearchUseSparkle';
export const CPE_SEARCH_REVERSE_WILDCARD = 'cpeSearchReverseWildcard';
export const DISABLE_SPARKLE_BASED_BUILD_CACHING = 'disableSparkleBasedBuildCaching';

/**
 * License Concluded Feature Flags
 */
export const LICENSE_CONCLUDED = 'licenseConcluded';

/**
 * Legacy Dependency Edit Modal Feature Flag
 * This is intended to be a TEMPORARY flag to alleviate some customer friction.
 * See https://github.com/fossas/FOSSA/pull/16861 for more details.
 */
export const LEGACY_DEP_EDIT_MODAL = 'legacyDepEditModal';

/* Hidden feature flags. These must be manually added to the database, and are not
 * available to be added via the admin panel. To add into the admin UI, you must
 * add below into `flagDescriptions`
 */
// UI for editing of Raw Vulns
export const RAW_VULN_EDIT = 'rawVulnEdit';
// Include all dependencies and licenses in the live report
export const FULL_LIVE_REPORT = 'fullLiveReport';
// Enables the Package Observability Inventory Feature
export const PACKAGE_INVENTORY = 'packageInventoryV2';
export const PACKAGE_INVENTORY_CACHE = 'packageInventoryCache';
export const DISABLE_PACKAGE_VULN_FILTER_CACHE = 'disablePackageVulnFilterCache';
// We want to selectively add this FF to certain orgs that do not want their users importing projects via the UI.
export const DISABLE_ADD_PROJECT_BUTTON = 'disableAddProjectButton';
export const GLOBAL_LICENSE_CORRECTIONS = 'globalLicenseCorrections';
// Changes "Ignore" to "Resolve" in some FE components. Please use this sparingly.
export const IGNORE_RESOLVE_RENAME = 'ignoreResolveRename';
// PDF Compliance Reports V2
export const DISABLE_PDF_COMPLIANCE_REPORTS_V2 = 'disablePDFComplianceReportsV2';
// Marks whether the issue-snapshot backfill has been finished for this org
export const BACKFILL_ISSUE_SNAPSHOTS_COMPLETE = 'backfillIssueSnapshotsComplete';
// Enable projects and teams columns in global package report
// Enables reachability details and filters for vuln issues.
export const ENABLE_REACHABILITY = 'reachability';
// User defined dependencies from the CLI don't require licenses, and can create a lot of noise and high number of issues
// if used in a specific way (binary scannning for example). This flag doesn't flag issues for any deps of fetcher `user`
export const SKIP_UNLICENSED_ISSUES_FOR_USER_DEFINED_DEPENDENCIES = 'skipUnlicensedIssuesForUserDefinedDependencies';
export const DISABLE_TASK_LOGS = 'disableTaskLogs';
export const VULN_RANGES_EXPORT = 'vulnRangesExport';
// Hard code a CycloneDX standard + attestation to CycloneDX JSON SBOMs
export const HARD_CODED_CYCLONEDX_ATTESTATION = 'hardCodedCycloneDXAttestation';
// Enables configuring API webhooks for issue notifications
export const API_WEBHOOK_NOTIFICATIONS = 'apiWebhookNotifications';
// Enables the FOSSA Command Palette (CMD + K/fast search/etc.)
export const COMMAND_PALETTE = 'commandPalette';
// Enables the integration with Code Sentry
export const BINARY_DECOMPOSITION = 'binaryDecomposition';
export const US_BANK_FIX_PLAN_HEADERS = 'usBankFixPlanHeaders';
//  Note: this is a Ford-only feature, which is why it's hidden.
export const TEAM_GROUPS = 'teamGroups';
// Enables the auto update feature with fossabot
export const AUTO_UPDATE = 'autoUpdate';
// Enables the SBOM Sharing feature
export const SBOM_SHARING = 'sbomSharing';
// Enables the new Inventory UI
export const INVENTORY_UI = 'inventoryUI';

/**
 * This is the Feature Flag that will be checked when deciding whether or not to restrict security features.
 */
export const FULL_SECURITY = 'fullSecurity';

/**
 * This is the Feature Flag that will be checked when deciding whether or not to restrict quality features.
 */
export const FULL_QUALITY = 'fullQuality';

type FlagDescription = {
  displayName: string;
  description: string;
  type: FlagType;
};

// Flag names and their descriptions to show in the UI
// NOTE: these are loosely ordered by popularity/time of creation
export const flagDescriptions: Record<string, FlagDescription> = {
  [QUALITY]: {
    displayName: 'Quality',
    description:
      'Turns limited quality features on for the organization. For full features, also enable Full Quality. If hidden, that means the subscription tier has it included already.',
    type: FlagType.Product,
  },
  [FULL_QUALITY]: {
    displayName: 'Full Quality',
    description:
      'Turns full quality features on for the organization. Without this, the org cannot customize quality policies.',
    type: FlagType.Product,
  },
  [SECURITY]: {
    displayName: 'Security',
    description: 'Turns limited security features on for the organization. (Legacy Billing)',
    type: FlagType.Product,
  },
  [FULL_SECURITY]: {
    displayName: 'Full Security',
    description: 'Turns full security features on for the organization. (Legacy Billing)',
    type: FlagType.Product,
  },
  [ARCHIVE_AND_LICENSE_UPLOAD]: {
    displayName: 'Archive + CLI License Scanning',
    description: 'Turns the archive upload functionality on, which includes CLI License Scanning.',
    type: FlagType.Product,
  },
  [DEPENDENCY_PATH_GLOBAL_ISSUES_REPORT]: {
    displayName: 'Dependency Path in Global Issues Report',
    description:
      'Adds the Dependency Path column to Global Issues CSV Report. The process is very expensive for non-provided projects.',
    type: FlagType.Tweaks,
  },
  [DISABLE_ORG_ALERT_BANNER]: {
    displayName: 'Disable Org Alert Banner',
    description: 'Disable super user external org warning banner when outside of the fossa org',
    type: FlagType.Configuration,
  },
  [LDAP_AUTHENTICATION]: {
    displayName: 'LDAP Authentication',
    description: 'Allows organizations to sign in to FOSSA via LDAP.',
    type: FlagType.Configuration,
  },
  [FILE_PATH_IN_REPORTS]: {
    displayName: 'File Path in Reports',
    description: 'Adds file path information to the reporting UI.',
    type: FlagType.Tweaks,
  },
  [TEAM_UNIQUE_ID]: {
    displayName: 'Team Unique ID',
    description: 'Adds ability to set a unique ID for a team across your organization.',
    type: FlagType.Customer,
  },
  [TEAM_GROUPS]: {
    displayName: 'Team Groups',
    description: 'Adds the Team Groups feature, which allows you to group teams.',
    type: FlagType.Customer,
  },
  [GROUPED_SLACK_NOTIFICATIONS]: {
    displayName: 'Grouped Slack Notifications',
    description: 'Group issue notifications into a single Slack message',
    type: FlagType.Alpha,
  },
  [VENDORED_DEPENDENCY_DEFAULT_TO_ARCHIVE_UPLOAD]: {
    displayName: 'Default to Archive Uploads',
    description: 'CLI defaults to Archive Uploads instead of CLI-side license scans for vendored dependency builds.',
    type: FlagType.Configuration,
  },
  [CLI_LICENSE_SCAN_REQUIRE_FULL_FILE_UPLOADS]: {
    displayName: 'Full-file uploads for CLI License scans',
    description:
      'CLI-side license scans upload full file contents instead of the match string. This uploads customer code to FOSSA\'s servers.',
    type: FlagType.Tweaks,
  },
  [CLI_DEFAULT_TO_FIRST_PARTY_SCANS]: {
    displayName: 'First-party scans for CLI',
    description: 'Default to license scanning the directory being analyzed by the CLI',
    type: FlagType.Configuration,
  },
  [DEDUPE_LOCATORS_IN_PLAINTEXT_REPORTS]: {
    displayName: 'De-dupe locators in Plaintext Reports',
    description:
      'De-duplicates matching locators from dependencies that are both direct and transitive at the same time and keeps only the direct one in plaintext reports for Projects & Release Groups',
    type: FlagType.Configuration,
  },
  [EXCLUDES_DEPENDENCY_PATHS_FROM_ISSUES_CSV_EXPORT]: {
    displayName: 'Exclude Dependency Paths from Issues Csv Export',
    description: `If enabled, dependency paths will not be included when downloading csv from global issues page.`,
    type: FlagType.Performance,
  },
  [REQUIRE_POLICY_ON_UPLOAD]: {
    displayName: 'Require Policy on Upload',
    description: 'Reject new projects uploaded from the CLI without an explicitly set policy.',
    type: FlagType.Configuration,
  },
  [REQUIRE_TEAM_ON_UPLOAD]: {
    displayName: 'Require Team on Upload',
    description: 'Reject new projects uploaded from the CLI and UI without an explicitly set team.',
    type: FlagType.Configuration,
  },
  [DISABLE_ADD_PROJECT_BUTTON]: {
    displayName: 'Disable Add Project Button',
    description: 'Disables the Add Project button on the projects dashboard.',
    type: FlagType.Configuration,
  },
  [DISABLE_PDF_COMPLIANCE_REPORTS_V2]: {
    displayName: 'Disable PDF Compliance Reports V2',
    description: 'Display PDF compliance reports using the old format.',
    type: FlagType.Product,
  },
  [ENABLE_REACHABILITY]: {
    displayName: 'Enable Reachability',
    description: 'Enables reachability on future analyses',
    type: FlagType.Configuration,
  },
  [SECURITY_PDF_REPORTS]: {
    displayName: 'Security PDF Reports',
    description:
      'Enables global and project level security PDF reports. Disabled by default in favor of Issue History reports',
    type: FlagType.Deprecated,
  },
  [SBOM_PORTAL]: {
    displayName: 'SBOM Portal',
    description: 'Allows an organization to create and publish to their own SBOM Portal',
    type: FlagType.Alpha,
  },
  [SBOM_PORTAL_PRIVATE]: {
    displayName: 'SBOM Portal Private',
    description: 'Allows an organization to configure a private SBOM Portal',
    type: FlagType.Product,
  },
  [DISABLE_ORG_BOM_V2]: {
    displayName: 'Disable Org Bom V2',
    description: 'Disables the Deps V2 rewrite of the Global Package Bundle Report and uses the old version.',
    type: FlagType.Configuration,
  },
  [VULN_RANGES_EXPORT]: {
    displayName: 'Vuln Ranges Export',
    description: 'Enables exporting of vulnerability version ranges to a CSV file.',
    type: FlagType.Customer,
  },
  [ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS]: {
    displayName: 'Enable Downloading Large Project Licensing Reports',
    description: 'Enables directly downloading large project licensing reports (which can be slow and potentially broken).',
    type: FlagType.Deprecated,
  },
  [HARD_CODED_CYCLONEDX_ATTESTATION]: {
    displayName: 'SBOM Attestations',
    description: 'Adds real attestations for hardcoded claims to SBOMs',
    type: FlagType.Customer,
  },
  [PER_PROJECT_USER_DEFINED_DEPS]: {
    displayName: 'Per-project UDPs',
    description: 'User-defined dependencies are scoped to a single project, and not reused across the organization.',
    type: FlagType.Tweaks,
  },
  [COMMAND_PALETTE]: {
    displayName: 'Command Palette',
    description: 'Enables the FOSSA Command Palette (CMD + K/fast search/etc.)',
    type: FlagType.Alpha,
  },
  [BINARY_DECOMPOSITION]: {
    displayName: 'Binary Decomposition through CodeSentry',
    description: 'Enables Binary Decomposition of uploaded archives through CodeSentry.',
    type: FlagType.Alpha,
  },
  [AUTO_UPDATE]: {
    displayName: 'Auto Update',
    description: 'Enables AUTO_UPDATE feature in fossabot.',
    type: FlagType.Alpha,
  },
  [DEPSV3_USE_SPARKLE]: {
    displayName: 'DepsV3: Use Sparkle',
    description: 'Read dependency graphs from Sparkle, falling back to DependencyLocks.',
    type: FlagType.Alpha,
  },
  [SBOM_SHARING]: {
    displayName: 'SBOM Sharing',
    description: 'Enables the SBOM Sharing feature',
    type: FlagType.Alpha,
  },
  [SNIPPET_DETECTION]: {
    displayName: 'Snippet Detection',
    description: 'Enables the Snippet Detection feature for detecting snippets in your codebase.',
    type: FlagType.Alpha,
  },
  [VENDORED_DEPENDENCY_DETECTION]: {
    displayName: 'Vendored Dependencies',
    description: 'Enables the Vendored Dependency feature for detecting vendored dependencies in your codebase.',
    type: FlagType.Alpha,
  },
  [CPE_SEARCH_USE_SPARKLE]: {
    displayName: 'Sparkle CPE Search',
    description: 'Search for CPE matches in Sparkle instead of the vulns service',
    type: FlagType.Beta,
  },
  [CPE_SEARCH_REVERSE_WILDCARD]: {
    displayName: 'CPE Reverse Wildcard Search',
    description: 'Requires Sparkle CPE Search. Performs reverse wildcard matching on CPEs.',
    type: FlagType.Product,
  },
  [ISSUES_CREATE_V2]: {
    displayName: 'Issues Create V2',
    description: 'Use the new implementation for creating issues',
    type: FlagType.Alpha,
  },
  [INVENTORY_UI]: {
    displayName: 'Inventory UI',
    description: 'Enables the new Inventory UI. Needed to view Snippets and Binary Analysis.',
    type: FlagType.Alpha,
  },
  [LICENSE_CONCLUDED]: {
    displayName: 'License Concluded',
    description: 'Enables various FE and BE changes related to the License Concluded work.',
    type: FlagType.Alpha,
  },
  [REPORT_COPYRIGHT_SANITIZATION]: {
    displayName: '[Hack] Report Copyright Sanitization',
    description: 'Sanitizes copyright notices in reports. This is a hack to unblock some customers.',
    type: FlagType.Customer,
  },
  [TEAM_UNIQUE_ID_IN_SAML]: {
    displayName: 'Team Unique ID in SAML',
    description: 'Set the team unique id when creating a new team via SAML log in.',
    type: FlagType.Customer,
  },
  [MANUAL_MALWARE]: {
    displayName: 'Manual Malware',
    description: 'Allow super admin to manually create, update, or delete malware reports.',
    type: FlagType.Alpha,
  },
  [ISSUES_MALWARE]: {
    displayName: 'Issues: Malware',
    description: 'Flag malicious package locators as "Malware"',
    type: FlagType.Alpha,
  },
  [DISABLE_SPARKLE_BASED_BUILD_CACHING]: {
    displayName: 'Disable Sparkle Based Build Caching',
    description: 'Don\'t skip queueing dependencies for builds if one has completed recently. On by default for all orgs.',
    type: FlagType.Configuration,
  },
  [REPORTS_V2]: {
    displayName: 'Reports V2',
    description: 'Enables the new Reports V2 feature.',
    type: FlagType.Alpha,
  },
  [LEGACY_DEP_EDIT_MODAL]: {
    displayName: 'Legacy Dependency Edit Modal',
    description: 'Disables the new Dependency Drawer and uses the old edit UI (deprecated soon)',
    type: FlagType.Customer,
  },
  [MFA]: {
    displayName: 'Multi-Factor Authentication',
    description: 'Enables the MFA feature.',
    type: FlagType.Alpha,
  },
  [MANUAL_DEPENDENCIES_REVAMP]: {
    displayName: 'Manual Dependencies Revamp',
    description: 'Enables the new user interface and experience for adding manual dependencies.',
    type: FlagType.Alpha,
  },
  [SAVED_REPORT_OPTIONS]: {
    displayName: 'Saved Report Options',
    description: 'Enables the ability to save report options for reuse.',
    type: FlagType.Alpha,
  },
} as const;
